import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { useAuth } from 'Hooks/useAuth';
import { useNotification } from 'Hooks/useNotification';
import { useFormValidation } from 'Hooks/useFormValidation';
import { ROUTES } from 'Utils/routes';
import { SignUpFormErrors } from 'Types/forms';
import Copyright from 'Components/Copyright/Copyright';
import FormTextField from 'Components/FormTextField/FormTextField';
import PasswordErrorList from 'Components/PasswordErrorList/PasswordErrorList';
import PasswordStrengthIndicator from 'Components/PasswordStrengthIndicator/PasswordStrengthIndicator';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Divider from '@mui/material/Divider';
import GoogleIcon from 'Components/Icons/GoogleIcon';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';

export default function SignUp() {
  const { signUp, signInWithGoogle, isOAuthEnabled } = useAuth();
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const {
    values,
    errors,
    setFieldValue,
    clearFieldError,
    validateEmail,
    validatePassword,
    validateAll,
  } = useFormValidation<SignUpFormErrors>({ email: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);

  // Clear password when navigating away for security
  useEffect(() => {
    return () => {
      setFieldValue('password', '');
    };
  }, [setFieldValue]);

  const validateForm = (): boolean => {
    return validateAll([() => validateEmail('email'), () => validatePassword('password')]);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    const result = await signUp(uuidv4(), values.email, values.password);
    setIsLoading(false);

    if (result.success) {
      navigate(ROUTES.CONFIRM_SIGN_UP, { replace: true });
    } else {
      showNotification(result.message);
    }
  };

  const handleGoogleSignUp = async () => {
    setIsGoogleLoading(true);
    const result = await signInWithGoogle();
    // Note: signInWithGoogle redirects to Google, so we only handle errors here
    if (!result.success) {
      setIsGoogleLoading(false);
      showNotification(result.message);
    }
  };

  return (
    <Container component='main' maxWidth='xs'>
      <CssBaseline />
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Avatar sx={{ m: 1, bgcolor: 'secondary.dark' }}>
          <LockOutlinedIcon />
        </Avatar>
        <Typography component='h1' variant='h5'>
          Sign up
        </Typography>
        <Box component='form' noValidate onSubmit={handleSubmit} sx={{ mt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormTextField
                required
                name='email'
                label='Email Address'
                autoComplete='email'
                value={values.email}
                fieldError={errors.email}
                disabled={isLoading}
                onFieldChange={setFieldValue}
                onClearError={clearFieldError}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                color='info'
                name='password'
                label='Password'
                type='password'
                id='password'
                autoComplete='new-password'
                value={values.password}
                error={!!errors.password}
                disabled={isLoading}
                inputProps={{
                  'aria-describedby': errors.password ? 'password-errors' : undefined,
                }}
                onChange={(e) => {
                  setFieldValue('password', e.target.value);
                  if (errors.password) clearFieldError('password');
                }}
              />
              <PasswordStrengthIndicator password={values.password} />
              <PasswordErrorList errors={errors.password || []} id='password-errors' />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Checkbox value='allowExtraEmails' color='info' disabled={isLoading} />}
                label='I want to receive inspiration, marketing promotions and updates via email.'
              />
            </Grid>
          </Grid>
          <Button
            type='submit'
            color='secondary'
            fullWidth
            variant='contained'
            disabled={isLoading || isGoogleLoading}
            sx={{ mt: 3, mb: 2 }}
          >
            {isLoading ? <CircularProgress size={24} color='inherit' /> : 'Sign up'}
          </Button>
          {isOAuthEnabled && (
            <>
              <Divider sx={{ my: 2 }}>or</Divider>
              <Button
                fullWidth
                variant='outlined'
                startIcon={<GoogleIcon />}
                disabled={isLoading || isGoogleLoading}
                onClick={handleGoogleSignUp}
                sx={{
                  mb: 2,
                  borderColor: 'grey.400',
                  color: 'text.primary',
                  '&:hover': {
                    borderColor: 'grey.600',
                    bgcolor: 'grey.50',
                  },
                }}
              >
                {isGoogleLoading ? (
                  <CircularProgress size={24} color='inherit' />
                ) : (
                  'Continue with Google'
                )}
              </Button>
            </>
          )}
          <Grid container justifyContent='flex-end'>
            <Grid item>
              <Link href={ROUTES.SIGN_IN} variant='body2' color='secondary.contrastText'>
                Already have an account? Sign in
              </Link>
            </Grid>
          </Grid>
        </Box>
      </Box>
      <Copyright sx={{ mt: 5 }} />
    </Container>
  );
}
